/**
 * lib/offer-selection.ts — the ONE place the PDP buy-box offer is picked.
 *
 * FRIDGE-122. Until now the buy box was simply the first row of a
 * price-ascending offers query (`retailers.find(r => !r.isPriceUnknown)` in
 * app/products/[slug]/product-client.tsx). That optimizes the shopper's price
 * and ignores what the click pays — on a multi-offer PDP it is a revenue
 * decision nobody was making on purpose.
 *
 * An honest "rank by expected commission" needs a per-retailer rate that came
 * from a network's own program terms. Live SQL, 08/16/2026:
 *
 *   advertisers       37 rows — 0 with commission_rate_bps, 0 with payout_rate,
 *                     0 with cookie_duration_days
 *   retailers         25 rows — 0 with commission_estimate_bps; 1 with a cookie
 *                     window (Amazon, 1 day), the other 24 blank
 *   retailer_offers   40,361 rows — 992 carry commission_rate_bps, and every
 *                     one of those 992 is the SAME 200 bps on an Amazon row,
 *                     with no program-terms source recorded anywhere
 *   multi-offer PDPs  1,973 active — of which 0 carry two offers that both
 *                     have a rate, and 5 carry exactly one rated offer
 *
 * The ranking input therefore does not exist yet. This module is written to
 * say so rather than to pretend: it ranks by expected commission ONLY when the
 * comparison stands up on real per-offer rates, and otherwise returns the
 * previous lowest-price pick unchanged together with the reason it could not
 * rank. `basis` is the measurement — while it reads 'lowest-price' with reason
 * 'no-rate-on-every-candidate', not one buy box on the site has moved.
 *
 * Two rules keep an unsourced number from quietly steering revenue:
 *
 *   1. EVERY fresh candidate must carry a rate. One rated offer against three
 *      blank ones is not a comparison — ranking on it would hand the buy box
 *      to whichever retailer happened to get filled in first.
 *   2. The candidates must carry at least TWO DIFFERENT rates. A single flat
 *      rate across the board — precisely the shape of today's 992 Amazon rows
 *      — says nothing about which retailer pays better, and ranking on
 *      price × flat-rate is just "the most expensive offer wins" wearing a
 *      commission costume. That is the failure this rule exists to stop, and
 *      it is the one a careless bulk write would otherwise trigger.
 *
 * The offer LIST is not reordered here. It stays price-ascending, so the
 * genuinely lowest price keeps the top of the rail and its "lowest" framing;
 * only which row is treated as the buy box can ever move.
 *
 * Framework-free on purpose (no 'use client', no React) so server components,
 * route handlers, routines and tests can all import it directly.
 */

export interface SelectableOffer {
  /** The PDP's own freshness gate: no price, or a price too old to show. */
  isPriceUnknown?: boolean
  priceCents?: number | null
  /** Per-offer rate in basis points, straight from retailer_offers. */
  commissionRateBps?: number | null
  /**
   * Retailer-level fallback (retailers.commission_estimate_bps). Read only
   * when the offer row itself has no rate, so the propagation step named in
   * FRIDGE-122 lands without a second wiring change here.
   */
  retailerCommissionBps?: number | null
}

export type BuyBoxBasis = 'expected-commission' | 'lowest-price' | 'none'

export type BuyBoxReason =
  /** Ranked: every candidate had a rate and the rates disagreed. */
  | 'ranked-by-expected-commission'
  /** Nothing on the PDP has a price we are willing to show. */
  | 'no-fresh-price'
  /** One fresh offer — there is nothing to rank against. */
  | 'single-candidate'
  /** At least one candidate has no rate, so the comparison is incomplete. */
  | 'no-rate-on-every-candidate'
  /** Every candidate carries the same rate, which cannot separate them. */
  | 'one-rate-across-all-candidates'

export interface BuyBoxSelection<T> {
  /** The offer the buy box, verdict CTA, sticky CTA and compare CTA point at. */
  offer: T | null
  basis: BuyBoxBasis
  reason: BuyBoxReason
  /** Fresh-priced offers that were in play. */
  candidates: number
  /** How many of those carried a usable rate. */
  rated: number
}

function usableBps(value: unknown): number | null {
  // A category excluded by contract pays zero; it must never inherit the
  // retailer's otherwise-positive default (FRIDGE-122).
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

/** The rate that applies to one offer: its own first, retailer fallback second. */
export function offerCommissionBps(offer: SelectableOffer): number | null {
  return usableBps(offer?.commissionRateBps) ?? usableBps(offer?.retailerCommissionBps)
}

/**
 * What one click is worth if it converts, in cents: price × rate ÷ 10,000.
 * Null whenever either input is missing — never a stand-in number.
 */
export function expectedCommissionCents(offer: SelectableOffer): number | null {
  const bps = offerCommissionBps(offer)
  if (bps === null) return null
  const price = offer?.priceCents
  if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0) return null
  return (price * bps) / 10000
}

/**
 * Pick the buy box from an offer rail that is ALREADY sorted the way the page
 * renders it: fresh prices ascending, price-unknown rows demoted to the end.
 * `offers[0]` is therefore the incumbent lowest-price pick, and it is what
 * comes back untouched every time the rate data cannot carry a decision.
 */
export function selectBuyBox<T extends SelectableOffer>(
  offers: readonly T[] | null | undefined,
): BuyBoxSelection<T> {
  const candidates = (offers ?? []).filter((o) => o && o.isPriceUnknown !== true)
  const lowestPrice = candidates[0] ?? null

  const expected = candidates.map((o) => expectedCommissionCents(o))
  const rated = expected.filter((v) => v !== null).length

  if (candidates.length === 0) {
    return { offer: null, basis: 'none', reason: 'no-fresh-price', candidates: 0, rated: 0 }
  }

  const fallback = (reason: BuyBoxReason): BuyBoxSelection<T> => ({
    offer: lowestPrice,
    basis: 'lowest-price',
    reason,
    candidates: candidates.length,
    rated,
  })

  if (candidates.length < 2) return fallback('single-candidate')
  if (rated < candidates.length) return fallback('no-rate-on-every-candidate')

  const rates = new Set(candidates.map((o) => offerCommissionBps(o)))
  if (rates.size < 2) return fallback('one-rate-across-all-candidates')

  let bestIndex = 0
  for (let i = 1; i < candidates.length; i += 1) {
    // Strictly greater keeps the earlier — i.e. cheaper — offer on a tie.
    if ((expected[i] as number) > (expected[bestIndex] as number)) bestIndex = i
  }

  return {
    offer: candidates[bestIndex],
    basis: 'expected-commission',
    reason: 'ranked-by-expected-commission',
    candidates: candidates.length,
    rated,
  }
}
